import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';



const EMPTY_TIMINGS = [];



const normalise = word => word.toLowerCase().replace(/[’‘]/g, "'").replace(/[^\p{L}\p{N}']/gu, '');



// Keep measured speech cues; estimate only script tokens absent from the ASR.



function buildTimeline(narration, wordTimings, duration) {



  const script = narration.match(/\S+/g) || [];



  const spoken = (Array.isArray(wordTimings) ? wordTimings : []).filter(item =>



    item && typeof item.word === 'string' && Number.isFinite(item.start) && Number.isFinite(item.end) && item.start >= 0 && item.end >= item.start);



  const length = duration > 0 ? duration : spoken.length ? spoken[spoken.length - 1].end : script.length / 2.5;



  const mapped = Array(script.length).fill(null);



  if (spoken.length) {



    const rows = script.length + 1, columns = spoken.length + 1;



    const scores = new Uint16Array(rows * columns);



    const source = script.map(normalise), audio = spoken.map(item => normalise(item.word));



    for (let i = 1; i < rows; i += 1) for (let j = 1; j < columns; j += 1) {



      scores[i * columns + j] = source[i - 1] && source[i - 1] === audio[j - 1]



        ? scores[(i - 1) * columns + j - 1] + 1



        : Math.max(scores[(i - 1) * columns + j], scores[i * columns + j - 1]);



    }



    let i = script.length, j = spoken.length;



    while (i > 0 && j > 0) {



      if (source[i - 1] && source[i - 1] === audio[j - 1]) {



        mapped[i - 1] = { ...spoken[j - 1], measured: true }; i -= 1; j -= 1;



      } else if (scores[(i - 1) * columns + j] >= scores[i * columns + j - 1]) i -= 1;



      else j -= 1;



    }



  }



  const measured = mapped.filter(Boolean).length;



  for (let index = 0; index < mapped.length;) {



    if (mapped[index]) { index += 1; continue; }



    const from = index;



    while (index < mapped.length && !mapped[index]) index += 1;



    const start = from ? mapped[from - 1].end : 0;



    const end = Math.max(start, index < mapped.length ? mapped[index].start : length);



    const span = (end - start) / (index - from);



    for (let k = from; k < index; k += 1) mapped[k] = {



      start: start + (k - from) * span, end: start + (k - from + 1) * span, measured: false,



    };



  }



  return { words: mapped, mode: measured === script.length ? 'measured' : measured ? 'mixed' : 'estimated' };



}



function wordOffset(narration, phrase) {



  const at = narration.indexOf(phrase);



  if (at < 0) throw new Error(`Missing narration cue: ${phrase}`);



  return narration.slice(0, at).trim().split(/\s+/).filter(Boolean).length;



}



function cueTime(narration, timeline, phrase) {



  return timeline.words[wordOffset(narration, phrase)]?.start ?? Infinity;



}



function buildBeats(narration, timeline, phrases, endPhrase) {



  const script = narration.match(/\S+/g) || [];



  const offsets = phrases.map(phrase => wordOffset(narration, phrase));



  const end = endPhrase ? wordOffset(narration, endPhrase) : script.length;



  return offsets.map((from, index) => ({



    start: timeline.words[from]?.start ?? Infinity,



    text: script.slice(from, offsets[index + 1] ?? end).join(' '),



    timings: timeline.words.slice(from, offsets[index + 1] ?? end).map(word => word.start),



  }));



}



function activeIndex(items, now) {



  let index = 0;



  items.forEach((item, i) => { if (now >= item.start) index = i; });



  return index;



}



// Read the audio clock each frame, but render only when a word boundary,



// pause, seek or playback rate changes. Native CSS handles the motion.



function useLessonClock({ time = 0, audioElement, audioRef, playbackId, audioStatus } = {}, timeline) {



  const boundaries = useMemo(() => timeline.words.map(word => word.start), [timeline]);



  const sourceTime = Number.isFinite(time) ? Math.max(0, time) : 0;



  const parentAnchor = useRef({ time: sourceTime, stamp: null });



  const controller = useRef({ time: sourceTime, boundary: -2, playing: false, rate: 1, syncId: 0 });



  const [clock, setClock] = useState({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1, syncId: 0, seeking: false });



  const boundaryAt = current => {



    let low = 0, high = boundaries.length;



    while (low < high) {



      const middle = (low + high) >>> 1;



      if (boundaries[middle] <= current + .005) low = middle + 1;



      else high = middle;



    }



    return low - 1;



  };



  useEffect(() => {



    const previous = parentAnchor.current;



    const stamp = performance.now();



    const elapsed = previous.stamp == null ? 0 : (stamp - previous.stamp) / 1000;



    const expected = previous.time + (controller.current.playing ? elapsed * controller.current.rate : 0);



    const seek = previous.stamp != null && Math.abs(sourceTime - expected) > .6;



    parentAnchor.current = { time: sourceTime, stamp };



    // The media element is authoritative when available; otherwise changes to



    // the parent's time keep the interpolated fallback clock anchored.



    if (!(audioElement ?? audioRef?.current) && seek) {



      controller.current.syncId += 1;



      controller.current.boundary = -2;



      controller.current.time = sourceTime;



      setClock({ now: sourceTime, playing: audioStatus === 'Playing', rate: 1,



        syncId: controller.current.syncId, seeking: true });



    }



  }, [sourceTime, audioElement, audioRef]);



  useEffect(() => {



    const media = audioElement ?? audioRef?.current;



    let frame;



    const read = () => {



      const state = controller.current;



      const playing = media ? !media.paused && !media.ended : audioStatus === 'Playing';



      const rate = media ? media.playbackRate || 1 : 1;



      const anchor = parentAnchor.current;



      const current = media ? Math.max(0, media.currentTime || 0) : Math.max(0,



        anchor.time + (playing && anchor.stamp != null ? (performance.now() - anchor.stamp) / 1000 * rate : 0));



      const boundary = boundaryAt(current);



      return { current, boundary, playing, rate };



    };



    const update = (force = false, seek = false) => {



      const sample = read(), state = controller.current;



      const rateChanged = sample.rate !== state.rate;



      if (seek || rateChanged) state.syncId += 1;



      state.time = sample.current;



      if (force || seek || rateChanged || sample.boundary !== state.boundary || sample.playing !== state.playing) {



        state.boundary = sample.boundary;



        state.playing = sample.playing;



        state.rate = sample.rate;



        setClock({ now: sample.current, playing: sample.playing, rate: sample.rate,



          syncId: state.syncId, seeking: seek || rateChanged });



      }



      return sample.playing;



    };



    const tick = () => { if (update()) frame = requestAnimationFrame(tick); };



    const play = () => { cancelAnimationFrame(frame); update(true); frame = requestAnimationFrame(tick); };



    const stop = () => { cancelAnimationFrame(frame); update(true); };



    const seek = () => { update(true, true); };



    const rate = () => { update(true); };



    const sync = () => { update(); };



    if (media) {



      media.addEventListener('play', play); media.addEventListener('pause', stop);



      media.addEventListener('ended', stop); media.addEventListener('seeked', seek);



      media.addEventListener('ratechange', rate); media.addEventListener('timeupdate', sync);



    }



    // Pause/resume changes play state without remounting every word.



    const oldBinding = controller.current.binding;



    if (!oldBinding || oldBinding.media !== media || oldBinding.playbackId !== playbackId || oldBinding.boundaries !== boundaries) {



      controller.current.syncId += 1;



    }



    controller.current.binding = { media, playbackId, boundaries };



    update(true);



    if (read().playing) frame = requestAnimationFrame(tick);



    return () => {



      cancelAnimationFrame(frame);



      if (media) {



        media.removeEventListener('play', play); media.removeEventListener('pause', stop);



        media.removeEventListener('ended', stop); media.removeEventListener('seeked', seek);



        media.removeEventListener('ratechange', rate); media.removeEventListener('timeupdate', sync);



      }



    };



  }, [audioElement, audioRef, playbackId, audioStatus, boundaries]);



  return clock;



}



const MotionClock = createContext({ playing: true, rate: 1, syncId: 0 });



function MotionScope({ clock, children }) {



  const value = useMemo(() => ({ playing: clock.playing, rate: clock.rate, syncId: clock.syncId }),



    [clock.playing, clock.rate, clock.syncId]);



  return <MotionClock.Provider value={value}>{children}</MotionClock.Provider>;



}



// Freeze delays at mount/cue/seek. Do not change animationDelay on every



// clock update: CSS runs the 380ms rise/fade smoothly between React renders.



function RevealText({ as: Element = 'span', text = '', segments, timings, start = 0, now = 0,



  delay = 0, complete = false, className = '' }) {



  const { playing, rate, syncId } = useContext(MotionClock);



  const pieces = segments || [{ text }];



  const ariaText = pieces.map(piece => piece.text).join(' ');



  const timingKey = timings?.join(',') ?? '';



  const origin = useMemo(() => now, [syncId, start, delay, complete, ariaText, timingKey, rate]);



  let wordIndex = 0;



  return <Element className={className} aria-label={ariaText}>



    {pieces.map((piece, pieceIndex) => <React.Fragment key={pieceIndex}>



      {pieceIndex > 0 ? ' ' : ''}



      <span className={piece.handwritten ? `training-handwritten ${piece.long ? 'is-long' : ''}` : 'training-plain'}>



        {piece.text.split(/\s+/).filter(Boolean).map((word, index, words) => {



          const indexInPhrase = wordIndex++;



          const revealAt = timings?.[indexInPhrase] ?? start + delay + indexInPhrase * .09;



          return <React.Fragment key={index}>



            <span key={syncId} className="training-reveal-word" aria-hidden="true" style={{



              animationDelay: `${complete ? -10 : (revealAt - origin) / rate}s`,



              animationDuration: `${.38 / rate}s`,



              animationPlayState: playing ? 'running' : 'paused',



            }}>{word}</span>{index < words.length - 1 ? ' ' : ''}



          </React.Fragment>;



        })}



      </span>



    </React.Fragment>)}



  </Element>;



}



// Phrase changes overlap briefly, without blurring the entire slide.



function SceneTransition({ sceneKey, start = 0, now, instant = false, children }) {



  const { playing, rate, syncId } = useContext(MotionClock);



  const origin = useMemo(() => now, [sceneKey, start, syncId, rate]);



  const previous = useRef({ key: sceneKey, syncId, children });



  const [outgoing, setOutgoing] = useState(null);



  useLayoutEffect(() => {



    const old = previous.current;



    const changed = old.key !== sceneKey;



    previous.current = { key: sceneKey, syncId, children };



    if (instant || old.syncId !== syncId) { setOutgoing(null); return undefined; }



    if (!changed) return undefined;



    setOutgoing(old);



    const timer = window.setTimeout(() => setOutgoing(null), 450 / rate);



    return () => window.clearTimeout(timer);



  }, [sceneKey, instant, syncId, rate]);



  useLayoutEffect(() => { previous.current.children = children; });



  return <div className="training-transition-shell">



    {outgoing && !instant && <div key={`out-${outgoing.key}`} className="training-scene-layer is-exiting" aria-hidden="true">{outgoing.children}</div>}



    <div key={`in-${sceneKey}-${syncId}`} className="training-scene-layer is-entering" style={{



      animationDelay: `${instant ? -10 : (start - origin) / rate}s`,



      animationDuration: `${.45 / rate}s`, animationPlayState: playing ? 'running' : 'paused',



    }}>{children}</div>



  </div>;



}



const REVEAL_STYLES = `



.training-plain {display:inline;}



.training-reveal-word {display:inline-block;opacity:0;transform:translateY(.36em);animation:training-word-in 380ms cubic-bezier(.18,.84,.22,1) both;}



.training-handwritten {display:inline-block;font-family:'Ugly Handwriting',cursive;font-size:1.12em;font-weight:400;letter-spacing:-.012em;line-height:.9;}



.training-handwritten.is-long {display:block;width:fit-content;max-width:100%;margin:.12em auto 0;}



.training-transition-shell {position:absolute;inset:0;overflow:hidden;}



.training-scene-layer {position:absolute;inset:0;}



.training-scene-layer.is-entering {z-index:2;animation:training-scene-enter 450ms cubic-bezier(.16,1,.3,1) both;}



.training-scene-layer.is-exiting {z-index:1;pointer-events:none;animation:training-scene-exit 450ms cubic-bezier(.4,0,.2,1) both;}



@keyframes training-word-in {to {opacity:1;transform:translateY(0);}}



@keyframes training-scene-enter {from {opacity:0;transform:translate3d(20px,10px,0);}to {opacity:1;transform:translate3d(0,0,0);}}



@keyframes training-scene-exit {from {opacity:1;transform:translate3d(0,0,0);}to {opacity:0;transform:translate3d(-18px,-8px,0);}}



.is-seeking .training-scene-layer.is-exiting {display:none;}



@media(prefers-reduced-motion:reduce){



 .training-reveal-word {animation:none;opacity:1;transform:none;}



 .training-scene-layer {animation:none!important;opacity:1;transform:none;filter:none;}



 .training-scene-layer.is-exiting {display:none;}



}



`;



const DEFINITION = 'Tribe is a free, invite-only social platform that helps strangers meet through shared, in-person experiences.';



const DEFINITION_CAPTIONS = [



  { cue: 'So let’s start', segments: [{ text: 'So let’s start with the basics' }] },



  { cue: 'what is Tribe', segments: [{ text: 'what is Tribe?' }] },



  { cue: 'Tribe is a free', segments: [{ text: 'Tribe is a free, invite-only social platform' }] },



  { cue: 'that helps strangers', segments: [{ text: 'that helps strangers meet' }] },



  { cue: 'through shared', segments: [{ text: 'through' }, { text: 'shared, in-person experiences', long: true }] },



];



const DIFFERENCES = [



  { id: 'hosts', title: 'Trained hosts', description: 'Keep the experience on track and help guests connect.', cue: 'First, each gathering', titleCue: 'a trained host' },



  { id: 'activities', title: 'Engaging activities', description: 'Give people something to enjoy together.', cue: 'Second, every gathering', titleCue: 'an engaging activity' },



  { id: 'community', title: 'Invite-only community', description: 'Bring together people who are likely to get along and add to the experience.', cue: 'Third, Tribe is invite-only', titleCue: 'Tribe is invite-only' },



  { id: 'platform', title: 'The Tribe platform', description: 'Continue the conversation after the gathering.', cue: 'Fourth, after the gathering', titleCue: 'through the Tribe platform' },



];



function DifferenceIcon({ type }) {



  return <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor"



    strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">



    {type === 'hosts' ? <>



      <circle cx="9" cy="8" r="3" />



      <path d="M3 20v-2a6 6 0 0 1 12 0v2M16 5a3 3 0 0 1 0 6M21 20v-2a6 6 0 0 0-4-5.65" />



    </> : type === 'activities' ? <>



      <rect x="3" y="5" width="18" height="16" rx="3" />



      <path d="M7 3v4M17 3v4M3 10h18M8 15l2 2 5-5" />



    </> : type === 'community' ? <>



      <rect x="5" y="10" width="14" height="11" rx="2" />



      <path d="M8 10V7a4 4 0 0 1 8 0v3M12 16v2" />



      <circle cx="12" cy="15" r="1" />



    </> : <>



      <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8v.5Z" />



      <path d="M8 12h.01M12 12h.01M16 12h.01" strokeWidth="2.5" />



    </>}



  </svg>;



}



const ACTIVITY_EXAMPLES = [



  { cue: 'Dinner & Karaoke', label: 'Dinner + Karaoke' },



  { cue: 'Thrifting & Matcha', label: 'Thrifting + Matcha' },



  { cue: 'Museum & Wine Bar', label: 'Museum + Wine Bar' },



  { cue: 'Yoga & Tea', label: 'Yoga + Tea' },



];



// Anchor each caption to its spoken phrase, then overlap its word motion.



// ASR gaps are speech pauses, not visual animation delays.



const DEFINITION_WORD_DURATION = .28;



const DEFINITION_CAPTION_STAGGER = 95;



const DEFINITION_REVIEW_STAGGER = 45;



const DEFINITION_REVIEW_HOLD = 1000;



// Each caption starts its own stagger at mount. Normal playback never uses



// negative catch-up delays, even if the parent clock arrives late.



function DefinitionText({ segments, text, wordDelay, reviewing = false, finishing = false, instant = false }) {



  const { playing, rate, syncId } = useContext(MotionClock);



  const pieces = segments || [{ text }];



  const label = pieces.map(piece => piece.text).join(' ');



  // A seek stays settled until the next caption or seek, rather than replaying



  // when the next clock update clears its transient seeking flag.



  const settled = useMemo(() => instant, [syncId]);



  let wordIndex = 0;



  return <h1 aria-label={label}>



    {pieces.map((piece, pieceIndex) => {



      const words = piece.text.split(/\s+/).filter(Boolean);



      return <React.Fragment key={pieceIndex}>



        {pieceIndex > 0 && !piece.long ? ' ' : ''}



        <span className={`definition-reference-segment ${piece.long ? 'is-long' : ''}`}>



          {words.map((word, index) => {



            const globalIndex = wordIndex++;



            return <React.Fragment key={index}>



              <span key={syncId} className="definition-reference-word" style={{



                animationDelay: `${settled ? -10 : globalIndex * wordDelay / 1000 / rate}s`,



                animationDuration: `${DEFINITION_WORD_DURATION / rate}s`,



                animationPlayState: reviewing || finishing || playing ? 'running' : 'paused',



              }} aria-hidden="true">{word}</span>{index < words.length - 1 ? ' ' : ''}



            </React.Fragment>;



          })}



        </span>



      </React.Fragment>;



    })}



  </h1>;



}



function DefinitionSequence({ caption, complete, now, seeking }) {



  const { rate, syncId } = useContext(MotionClock);



  const [reviewReady, setReviewReady] = useState(false);



  const captionEnteredAt = useRef(now).current;



  // Snapshot the remaining motion at completion. The audio clock stops at



  // ended, so use one timeout rather than polling or continuing React frames.



  const finalMotionRemaining = useMemo(() => {



    const count = caption.segments.map(piece => piece.text).join(' ').split(/\s+/).length;



    const lastWord = captionEnteredAt + (count - 1) * DEFINITION_CAPTION_STAGGER / 1000;



    return complete ? Math.max(0, lastWord + DEFINITION_WORD_DURATION - now) / rate : 0;



  }, [complete, caption, rate, syncId, captionEnteredAt]);



  useEffect(() => {



    setReviewReady(false);



    if (!complete) return undefined;



    if (seeking) { setReviewReady(true); return undefined; }



    const timer = window.setTimeout(() => setReviewReady(true), finalMotionRemaining * 1000 + DEFINITION_REVIEW_HOLD);



    return () => window.clearTimeout(timer);



  }, [complete, seeking, finalMotionRemaining]);



  return complete && reviewReady ? <DefinitionReview now={now} seeking={seeking} /> :



    <div className="definition-caption">



      <DefinitionText segments={caption.segments} wordDelay={DEFINITION_CAPTION_STAGGER}



        finishing={complete} instant={seeking} />



    </div>;



}



function DefinitionReview({ now, seeking }) {



  return <div className="definition-review">



    <div className="definition-review-lockup">



      <p className="definition-eyebrow">What is Tribe?</p>



      <DefinitionText text={DEFINITION} wordDelay={DEFINITION_REVIEW_STAGGER} reviewing instant={seeking} />



    </div>



  </div>;



}



function useNarrationTimeline(narration, wordTimings, duration) {



  const timingKey = JSON.stringify(Array.isArray(wordTimings)



    ? wordTimings.map(item => [item?.word, item?.start, item?.end]) : []);



  return useMemo(() => buildTimeline(narration, wordTimings, duration), [narration, duration, timingKey]);



}



function WhatIsTribeScene(props) {



  const { part = 0, duration = 0, wordTimings = props.words ?? EMPTY_TIMINGS, audioStatus, playbackId = 0 } = props;



  const narration = WhatIsTribe.parts[part]?.narration || '';



  const timeline = useNarrationTimeline(narration, wordTimings, duration);



  const clock = useLessonClock(props, timeline);



  const { now, seeking } = clock;



  const complete = audioStatus === 'Complete' || now >= (timeline.words.at(-1)?.end ?? Infinity);



  const captions = useMemo(() => part === 0 ? DEFINITION_CAPTIONS.map(caption => {



    const offset = wordOffset(narration, caption.cue);



    const count = caption.segments.map(segment => segment.text).join(' ').split(/\s+/).length;



    return { ...caption, start: timeline.words[offset]?.start ?? Infinity,



      timings: timeline.words.slice(offset, offset + count).map(word => word.start) };



  }) : [], [part, narration, timeline]);



  const differences = useMemo(() => part === 1 ? DIFFERENCES.map(item => ({ ...item,



    start: cueTime(narration, timeline, item.cue), titleStart: cueTime(narration, timeline, item.titleCue),



  })) : [], [part, narration, timeline]);



  const examples = useMemo(() => part === 1 ? ACTIVITY_EXAMPLES.map(example => ({ ...example, start: cueTime(narration, timeline, example.cue) })) : [], [part, narration, timeline]);



  const review = part === 1 && (complete || now >= cueTime(narration, timeline, 'The combination'));



  const currentIndex = part === 1 && now >= differences[0].start ? activeIndex(differences, now) : -1;



  const example = part === 1 && !review && currentIndex === 1 && now >= examples[0].start && now < cueTime(narration, timeline, 'or something else')



    ? examples[activeIndex(examples, now)] : null;



  const restoration = part === 1 ? cueTime(narration, timeline, 'or something else') : Infinity;



  const captionIndex = part === 0 ? (complete ? captions.length - 1 : activeIndex(captions, now)) : 0;



  const caption = captions[captionIndex];



  return <MotionScope clock={clock}><section className={`tribe-concept-slide ${seeking ? 'is-seeking' : ''}`} data-timing-mode={timeline.mode}>



    {part === 0 ? <DefinitionSequence key={`${playbackId}-${captionIndex}`}



      caption={caption} complete={complete} now={now} seeking={seeking} /> : currentIndex < 0 && !review ?



      <div className="tribe-concept-intro" key={`${part}-${playbackId}-intro`}>



        <DefinitionText text="Four things make Tribe events special." wordDelay={DEFINITION_CAPTION_STAGGER} instant={seeking} />



      </div> : <div className={`difference-field ${review ? 'is-reviewing' : ''}`}>



        <RevealText as="h1" className="difference-field-title" text="What makes Tribe events special?"



          start={differences[0].start} now={now} complete={review} />



        <div className="difference-field-items">



          {differences.map((difference, index) => {



            const revealed = review || index <= currentIndex;



            const current = !review && index === currentIndex;



            const activeExample = current && index === 1 ? example : null;



            const restored = index === 1 && now >= restoration;



            const description = activeExample?.label ?? difference.description;



            const descriptionStart = activeExample?.start ?? (restored ? restoration : difference.titleStart + .42);



            return <div className={`difference-field-item ${revealed ? 'is-revealed' : ''} ${current ? 'is-current' : ''}`} key={difference.id} aria-hidden={!revealed}>



              <div className="difference-icon-marker" aria-hidden="true">

              <span className="difference-icon-circle">

                <DifferenceIcon type={difference.id} />

                <span className="difference-icon-number">{index + 1}</span>

              </span>

              <span className="difference-icon-stem" />

              <span className="difference-icon-dot" />

            </div>



              <RevealText as="h2" text={difference.title} start={difference.titleStart} now={now} complete={review} />



              <RevealText as="p" key={description} className={activeExample ? 'is-activity-example' : ''} text={description} start={descriptionStart} now={now} complete={review || index < currentIndex} />



            </div>;



          })}



        </div>



      </div>}



  </section></MotionScope>;



}



export default function WhatIsTribe(props = {}) {



  return <div className="host-background-slide host-tribe-slide" aria-label={WhatIsTribe.title}>



    <style>{HEADLINE_FONT_IMPORT + REVEAL_STYLES + TRIBE_STYLES + DESIGN_STYLES + DEFINITION_STYLES + DIFFERENCE_CARD_STYLES + HOST_MATCH_STYLES}</style><WhatIsTribeScene {...props} />



  </div>;



}



const TRIBE_STYLES = `



.host-tribe-slide {width:100%;background:#fff;color:#111;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;}



.host-tribe-slide * {box-sizing:border-box;}



.tribe-concept-slide {position:relative;width:100%;min-height:530px;overflow:hidden;color:#111;background:#fff;font-family:inherit;}



.definition-caption,.definition-review,.tribe-concept-intro {position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:36px 48px;}



.definition-caption h1,.tribe-concept-intro h1 {width:min(1080px,100%);margin:0;font-family:inherit;font-size:clamp(38px,5.4vw,70px);font-weight:750;line-height:1.1;letter-spacing:-.04em;text-align:center;}



.definition-review h1 {width:min(900px,100%);margin:0;font-family:inherit;font-size:clamp(30px,3.7vw,48px);font-weight:750;line-height:1.12;letter-spacing:-.035em;text-align:center;}



.difference-field {position:relative;min-height:530px;display:flex;flex-direction:column;justify-content:flex-start;padding:32px 48px 46px;}



.difference-field-title {margin:0;font-family:inherit;font-size:clamp(29px,3.25vw,43px);font-weight:750;line-height:1.12;letter-spacing:-.035em;text-align:center;}



.difference-field-title .training-handwritten {margin-left:.12em;}



.difference-field-items {display:grid;grid-template-columns:repeat(4,minmax(0,1fr));width:100%;max-width:1200px;margin:80px auto 0;}



.difference-field-item {min-width:0;min-height:215px;padding:0 24px;color:#111;border-left:1px solid #dedede;opacity:0;transition:opacity 420ms ease;}



.difference-field-item:first-child {border-left:0;padding-left:0;}



.difference-field-item:last-child {padding-right:0;}



.difference-field-item.is-revealed {opacity:.3;}



.difference-field-item.is-current,.difference-field.is-reviewing .difference-field-item {opacity:1;}



.difference-number {display:grid;place-items:center;width:30px;height:30px;border:1px solid #d8d8d8;border-radius:50%;font-size:12px;font-weight:500;line-height:1;font-variant-numeric:tabular-nums;}



.difference-field-item h2 {margin:20px 0 12px;font-family:inherit;font-size:clamp(19px,1.9vw,23px);font-weight:650;line-height:1.25;letter-spacing:-.022em;}



.difference-field-item p {margin:0;max-width:280px;color:currentColor;font-family:inherit;font-size:clamp(16px,1.45vw,19px);font-weight:400;line-height:1.55;letter-spacing:-.014em;}



.difference-field-item p.is-activity-example {font-weight:550;}



.host-training .ht-lesson-layout:has(.host-tribe-slide) {display:block;padding:0;}



.host-training .ht-lesson-layout:has(.host-tribe-slide)>.ht-lesson-copy {display:none;}



.host-training .ht-lesson-visual:has(.host-tribe-slide) {display:block;padding:0;background:#fff;border:0;border-radius:0;}



@media(max-width:1100px){



 .difference-field-items {grid-template-columns:repeat(2,minmax(0,1fr));row-gap:36px;margin-top:54px;}



 .difference-field-item {min-height:170px;}



 .difference-field-item:nth-child(3) {border-left:0;padding-left:0;}



 .difference-field-item:nth-child(2) {padding-right:0;}



}



@media(max-width:700px){



 .tribe-concept-slide {min-height:540px;}



 .definition-caption,.definition-review,.tribe-concept-intro {padding:28px 24px;}



 .definition-caption h1,.tribe-concept-intro h1 {font-size:clamp(32px,8.4vw,46px);}



 .definition-review h1 {font-size:clamp(28px,7vw,36px);}



 .difference-field {padding:28px 20px 38px;}



 .difference-field-title {font-size:clamp(27px,6.6vw,36px);}



 .difference-field-items {grid-template-columns:1fr;row-gap:0;margin-top:38px;}



 .difference-field-item,.difference-field-item:first-child,.difference-field-item:nth-child(3) {min-height:0;padding:20px 0;border-left:0;border-top:1px solid #dedede;}



 .difference-field-item:first-child {border-top:0;}



 .difference-field-item h2 {margin:14px 0 8px;font-size:22px;}



 .difference-field-item p {max-width:100%;font-size:16px;}



 .difference-number {width:27px;height:27px;font-size:11px;}



}



@media(prefers-reduced-motion:reduce){.difference-field-item {transition:none;}}



.tribe-concept-slide.is-seeking .difference-field-item {transition:none;}



`;



Object.assign(WhatIsTribe, {



  slide: 2, id: 'what-is-tribe', title: 'What Is Tribe?', section: 'Background',



  parts: [



    { part: 0, narration: "So let’s start with the basics: what is Tribe? Tribe is a free, invite-only social platform that helps strangers meet through shared, in-person experiences." },



    { part: 1, narration: "Four things make Tribe events special.\n\nFirst, each gathering is led by a trained host who keeps the experience on track, helps the group feel comfortable, and makes connecting easier.\n\nSecond, every gathering is thoughtfully designed and built around an engaging activity. That could be Dinner & Karaoke, Thrifting & Matcha, Museum & Wine Bar, Yoga & Tea, or something else people can enjoy together.\n\nThird, Tribe is invite-only, which allows us to thoughtfully curate the community and bring together people who are likely to get along and add to the experience.\n\nFourth, after the gathering, members can continue talking through the Tribe platform.\n\nThe combination of great hosts, intentional event design with engaging activities, a thoughtfully curated community, and the Tribe platform creates an experience that consistently entertains our members and helps them form meaningful connections." },



  ],



  summary: ['A free, invite-only social platform.', 'Thoughtfully designed, host-led experiences.', 'An invite-only, thoughtfully curated community.', 'Real connections that continue after the event.'],



});



const DESIGN_STYLES = `



.welcome-simple,.host-event-structure,.host-tribe-slide,.host-role-slide {



 --training-heading-size:clamp(29px,3.25vw,43px);



 --training-hero-size:clamp(38px,5.4vw,70px);



 font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;



 -webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility;



}



.welcome-simple .welcome-simple-roadmap {max-width:1040px;padding-top:36px;}



.welcome-simple .welcome-simple-roadmap h1 {text-align:center;font-size:clamp(24px,2.55vw,32px);margin-bottom:72px;}



.welcome-simple .welcome-simple-caption,.host-event-structure .event-flow-opening-copy,.definition-caption h1,.tribe-concept-intro h1,.host-role-slide .role-heading {



 max-width:850px;font-size:clamp(32px,4vw,50px);line-height:1.12;letter-spacing:-.035em;



}



.welcome-simple .welcome-simple-number {width:26px;height:26px;font-size:11px;font-weight:500;font-variant-numeric:tabular-nums;}



.welcome-simple .welcome-simple-grid h2,.difference-field-item h2,.host-role-slide .role-column-heading h2 {font-size:clamp(18px,1.7vw,21px);line-height:1.3;}



.welcome-simple .welcome-simple-grid p,.difference-field-item p {font-size:clamp(13px,1.15vw,15px);line-height:1.55;}



.difference-field-title,.host-event-structure .event-flow-heading,.host-role-slide .is-open .role-heading {font-size:clamp(24px,2.55vw,32px);line-height:1.15;}



.difference-field {padding-top:36px;}



.difference-field-items {max-width:1040px;margin-top:80px;}



.difference-number,.host-role-slide .role-column-number {width:26px;height:26px;font-size:11px;}



.host-role-slide .role-column-number {flex-basis:26px;}



.host-role-slide .role-table {max-width:960px;}



.host-role-slide .role-table-cell p {font-size:clamp(18px,1.7vw,22px);}



.definition-review h1 {max-width:780px;font-size:clamp(27px,3vw,38px);}



.host-event-structure .event-flow-scene {min-height:530px;}



.host-event-structure .event-flow-heading {top:36px;}



.host-event-structure .journey-diagrams {max-width:1120px;margin-top:136px;}



.host-event-structure .journey-stage-label {font-size:21px;font-weight:650;}



.host-event-structure .journey-stage-number {font-size:30px;font-weight:500;}



@media(max-width:700px){



 .welcome-simple,.host-event-structure,.host-tribe-slide,.host-role-slide {



  --training-heading-size:clamp(27px,6.6vw,36px);--training-hero-size:clamp(32px,8.4vw,46px);



 }



 .host-event-structure .event-flow-scene {min-height:540px;}



 .welcome-simple .welcome-simple-caption,.host-event-structure .event-flow-opening-copy,.definition-caption h1,.tribe-concept-intro h1,.host-role-slide .role-heading {font-size:clamp(28px,7.6vw,38px);}



 .welcome-simple .welcome-simple-roadmap h1,.difference-field-title,.host-event-structure .event-flow-heading,.host-role-slide .is-open .role-heading {font-size:clamp(23px,5.8vw,29px);}



 .host-event-structure .event-flow-heading {top:28px;}



 .host-event-structure .journey-diagrams {max-width:460px;margin-top:112px;}



 .welcome-simple .welcome-simple-grid p,.difference-field-item p {font-size:14px;}



 .difference-field-items {margin-top:40px;}



 .host-role-slide .role-column-heading h2 {font-size:clamp(16px,4vw,19px);}



 .host-role-slide .role-table-cell p {font-size:clamp(16px,4vw,19px);}



}



`;



const HEADLINE_FONT_IMPORT = "@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@700&display=swap');";



const DEFINITION_STYLES = `



.host-tribe-slide h1 {



 font-family:'Merriweather',Georgia,'Times New Roman',serif;



 font-weight:700;font-style:normal;letter-spacing:-.015em;



}



.host-tribe-slide h1 .training-handwritten {



 font-family:inherit;font-weight:inherit;font-size:inherit;line-height:inherit;



 letter-spacing:inherit;transform:none;



}



.host-tribe-slide .definition-caption h1 {max-width:800px;font-size:clamp(28px,3.2vw,40px);line-height:1.25;}



.host-tribe-slide .definition-review-lockup {width:min(760px,100%);text-align:center;}



.host-tribe-slide .definition-eyebrow {



 margin:0 0 22px;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;



 color:#5a5a5a;font-size:12px;font-weight:700;line-height:1.4;letter-spacing:.025em;



}



.host-tribe-slide .definition-review h1 {width:100%;max-width:none;margin:0;text-align:center;font-size:clamp(26px,2.7vw,34px);line-height:1.35;}



.host-tribe-slide .definition-reference-word {



 display:inline-block;opacity:0;transform:translateY(.16em);



 animation:definition-reference-word-in 280ms cubic-bezier(.18,.84,.22,1) both;



}



.host-tribe-slide .definition-reference-segment {display:inline;}



.host-tribe-slide .definition-reference-segment.is-long {



 display:block;width:fit-content;max-width:100%;margin:.12em auto 0;



}



@keyframes definition-reference-word-in {to {opacity:1;transform:translateY(0);}}



@media(max-width:700px){



 .host-tribe-slide .definition-caption h1 {font-size:clamp(26px,6.6vw,34px);}



 .host-tribe-slide .definition-review h1 {font-size:clamp(24px,5.7vw,30px);}



 .host-tribe-slide .definition-eyebrow {margin-bottom:18px;font-size:11px;}



}



@media(prefers-reduced-motion:reduce){



 .host-tribe-slide .definition-reference-word {animation:none;opacity:1;transform:none;}



}



`;







const DIFFERENCE_CARD_STYLES = `



.host-tribe-slide .tribe-concept-intro h1 {



 max-width:800px;font-family:'Merriweather',Georgia,'Times New Roman',serif;



 font-size:clamp(28px,3.2vw,40px);font-weight:700;line-height:1.25;letter-spacing:-.015em;



}



.host-tribe-slide .difference-field-title {



 font-family:'Merriweather',Georgia,'Times New Roman',serif;



 font-size:35px;font-weight:700;line-height:1.25;letter-spacing:-.015em;



}



.host-tribe-slide .difference-field-items {max-width:1040px;column-gap:34px;row-gap:36px;margin-top:58px;}



.host-tribe-slide .difference-field-item,



.host-tribe-slide .difference-field-item:first-child,



.host-tribe-slide .difference-field-item:nth-child(3),



.host-tribe-slide .difference-field-item:last-child {



 position:relative;display:flex;flex-direction:column;align-items:center;min-height:205px;padding:0;border:0;text-align:center;



}



.host-tribe-slide .difference-field-item:not(:first-child)::before {



 position:absolute;top:0;bottom:0;left:-17px;border-left:1px dashed #dedede;content:'';



}



.host-tribe-slide .difference-icon-circle {



 display:grid;place-items:center;flex:0 0 68px;width:68px;height:68px;



 border:0;border-radius:50%;color:#111;background:#f1f1f1;



}



.host-tribe-slide .difference-icon-circle svg {display:block;width:32px;height:32px;}



.host-tribe-slide .difference-field-item h2 {



 margin:20px 0 10px;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;



 font-size:clamp(18px,1.7vw,21px);font-weight:650;line-height:1.3;letter-spacing:-.022em;



}



.host-tribe-slide .difference-field-item p {max-width:235px;font-size:14px;line-height:1.55;}



@media(max-width:1100px){



 .host-tribe-slide .difference-field-items {max-width:760px;column-gap:40px;row-gap:36px;}



 .host-tribe-slide .difference-field-item:not(:first-child)::before {left:-20px;}



 .host-tribe-slide .difference-field-item:nth-child(odd)::before {display:none;}



}



@media(max-width:700px){



 .host-tribe-slide .tribe-concept-intro h1 {font-size:clamp(26px,6.6vw,34px);}



 .host-tribe-slide .difference-field-title {font-size:35px;}



 .host-tribe-slide .difference-field-items {margin-top:42px;row-gap:32px;}



 .host-tribe-slide .difference-field-item,



 .host-tribe-slide .difference-field-item:first-child,



 .host-tribe-slide .difference-field-item:nth-child(3),



 .host-tribe-slide .difference-field-item:last-child {min-height:0;padding:0;border:0;}



 .host-tribe-slide .difference-field-item:not(:first-child)::before {



  display:block;top:-16px;bottom:auto;left:0;right:0;border-left:0;border-top:1px dashed #dedede;



 }



 .host-tribe-slide .difference-icon-circle {flex-basis:62px;width:62px;height:62px;}



 .host-tribe-slide .difference-field-item h2 {margin-top:16px;font-size:20px;}



 .host-tribe-slide .difference-field-item p {max-width:290px;font-size:14px;}



}



`;





// Match the graphical and type sizes used by Host Responsibilities.

// The existing item dividers, reveal cues, examples and focus remain in place.

const HOST_MATCH_STYLES = `

.host-tribe-slide .difference-field-title {

  font-size:clamp(24px,2.55vw,32px);line-height:1.15;

}

.host-tribe-slide .difference-icon-marker {

  display:flex;flex-direction:column;align-items:center;margin:0 0 22px;

}

.host-tribe-slide .difference-icon-circle {

  position:relative;display:flex;align-items:center;justify-content:center;flex:0 0 auto;

  box-sizing:border-box;width:88px;height:88px;border:1.3px solid #111;

  border-radius:50%;background:transparent;color:#111;

}

.host-tribe-slide .difference-icon-circle svg {display:block;width:34px;height:34px;fill:none;stroke-width:1.35;}

.host-tribe-slide .difference-icon-number {

  position:absolute;top:-5px;right:-5px;display:grid;place-items:center;

  box-sizing:border-box;width:26px;height:26px;border:0;border-radius:50%;

  background:#ededed;color:#111;font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;

  font-size:11px;font-weight:600;line-height:1;font-variant-numeric:tabular-nums;

}

.host-tribe-slide .difference-icon-stem {width:1px;height:31px;background:#111;}

.host-tribe-slide .difference-icon-dot {width:5px;height:5px;border-radius:50%;background:#111;}

.host-tribe-slide .difference-field-item h2 {

  min-height:0;margin:0 0 12px;color:#111;white-space:nowrap;

  font-family:'Merriweather',Georgia,'Times New Roman',serif;

  font-size:18px;font-weight:700;line-height:1.45;letter-spacing:-.015em;

}

.host-tribe-slide .difference-field-item p {

  max-width:230px;margin:0 auto;color:#656565;

  font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;

  font-size:14px;line-height:1.65;letter-spacing:-.012em;

}

@media(min-width:701px) and (max-width:900px){

  .host-tribe-slide .difference-field-item h2 {font-size:15px;}

  .host-tribe-slide .difference-field-item p {font-size:13px;}

}

@media(max-width:700px){

  .host-tribe-slide .difference-field-title {font-size:clamp(22px,5.8vw,32px);}

  .host-tribe-slide .difference-icon-marker {margin-bottom:20px;}

  .host-tribe-slide .difference-icon-circle {width:76px;height:76px;flex-basis:auto;}

  .host-tribe-slide .difference-icon-circle svg {width:30px;height:30px;}

  .host-tribe-slide .difference-icon-number {width:24px;height:24px;font-size:10px;}

  .host-tribe-slide .difference-icon-stem {height:26px;}

  .host-tribe-slide .difference-field-item h2 {min-height:0;margin:0 0 12px;font-size:15px;line-height:1.5;}

  .host-tribe-slide .difference-field-item p {max-width:230px;font-size:13px;line-height:1.65;}

}

@media(max-width:360px){

  .host-tribe-slide .difference-field-item h2 {font-size:13px;}

}

`;
